<template>
  <div class="mb-15">
    <v-breadcrumbs :items="breadcrumbs"></v-breadcrumbs>
    <h1>クラス日誌作成</h1>

    <v-row>
      <v-col cols="6">
        <v-select
          v-model="classId"
          :items="classes"
          :item-title="(item) => item.classId === undefined ? null : `(${item.year}) ${item.className}`" 
          item-value="classId"
          variant="outlined"
          hide-details
          density="compact"
        ></v-select>
      </v-col>
      <v-col cols="6">
        <v-text-field type="date" variant="outlined" density="compact" v-model="date"> </v-text-field>
      </v-col>
    </v-row>

    <div class="mt-2 mb-4" v-if="existingDiary">
      <div class="existing-error-message">
        {{ selectedClassName }}
        {{ formattedDate }} の園日誌はすでに存在しています。
        <a
          class="ml-4"
          :href="`/class-diaries/${existingDiary.classId}/${existingDiary.classDiaryId}`"
          >編集ページへ</a
        >
      </div>
    </div>

    <v-row>
      <v-col>
        <h3 class="mb-2">ねらい</h3>
        <v-textarea variant="outlined" v-model="aim"></v-textarea>
      </v-col>
      <v-col> </v-col>
    </v-row>

    <v-row>
      <v-col>
        <h3 class="mb-2">活動内容: 午前</h3>
        <v-textarea variant="outlined" v-model="activityAm"></v-textarea>
      </v-col>
      <v-col>
        <h3 class="mb-2">活動内容: 午後</h3>
        <v-textarea variant="outlined" v-model="activityPm"></v-textarea>
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <h3 class="mb-2">反省と評価</h3>
        <v-textarea variant="outlined" v-model="assessment"></v-textarea>
      </v-col>
      <v-col>
        <h3 class="mb-2">保護者支援</h3>
        <v-textarea variant="outlined" v-model="support"></v-textarea>
      </v-col>
    </v-row>

    <div>
      <v-progress-linear
        v-if="isSaving"
        indeterminate
        color="primary"
        class="my-8"
      ></v-progress-linear>
    </div>

    <v-row>
      <v-spacer></v-spacer>
      <v-select
        variant="outlined"
        label="申請者"
        :items="staffOptions"
        item-value="userId"
        item-title="name"
        v-model="appliedBy"
      ></v-select>
      <v-spacer></v-spacer>
    </v-row>

    <v-row>
      <v-spacer></v-spacer>
      <v-btn
        color="primary"
        variant="outlined"
        x-large
        :disabled="(!isContentValid && !isSaving) || existingDiary"
        class="mx-2"
        @click="createAsDraft"
        >下書きとして保存</v-btn
      >
      <v-btn
        color="primary"
        variant="flat"
        x-large
        :disabled="
          (!isContentValid && !isSaving) || existingDiary || !appliedBy
        "
        class="mx-2"
        @click="createAndApply"
        >保存して申請</v-btn
      >
      <v-spacer></v-spacer>
    </v-row>
  </div>
</template>
<script lang="ts">
import { defineComponent } from "vue";

import { ClassDiaryResponse, NurseryClassResponse } from "chaild-api/lib";
import { classModule } from "@/store/dataModules/classModule";
import dayjs from "dayjs";
import { classDiaryModle } from "@/store/dataModules/classDiaryModue";
import ApiClassDiary from "@/api/ApiClassDiary";
import LocalDataService from "@/service/LocalDataService";
import { nurseryUserModule } from "@/store/dataModules/nurseryUserModule";
import { PrimaryColumn } from "typeorm";

interface DataType {
  classId: number | null;
  date: string;
  aim: string;
  activityAm: string;
  activityPm: string;
  assessment: string;
  support: string;
  appliedBy: number | null;

  isSaving: boolean;

  existingError: boolean;
  existingDiary: ClassDiaryResponse | null;
}

interface BreadcrumbItem {
  title: string;
  disabled: boolean;
  href: string;
  color: string;
}

export default defineComponent({
  computed: {
    breadcrumbs(): Array<BreadcrumbItem> {
      return [
        {
          title: "クラス日誌一覧",
          href: "/class-diaries",
          disabled: false,
          color: "primary"
        },
        {
          title: "新規作成",
          href: "",
          disabled: true,
          color: "grey"
        },
      ];
    },
    classes(): NurseryClassResponse[] {
      return classModule.classes;
    },
    isContentValid(): boolean {
      if (this.date && this.date.trim().length > 0) {
        return true;
      }
      return false;
    },
    selectedClassName(): string {
      if (this.classId) {
        const find = this.classes.find((c) => c.classId === this.classId);
        if (find) {
          return find.className;
        }
      }
      return "";
    },
    formattedDate() {
      return dayjs(this.date).format("YYYY年MM月DD日");
    },
    staffOptions() {
      return nurseryUserModule.staffs.filter(s => !s.isHidden).map((staff) => {
        return {
          userId: staff.userId,
          name: `${staff.lastName} ${staff.firstName}`,
        };
      });
    },
  },

  methods: {
    async createAndApply() {
      if (this.classId && this.appliedBy) {
        this.isSaving = true;
        await classDiaryModle.createDiary({
          classId: this.classId,
          date: this.date,
          aim: this.aim,
          activityAm: this.activityAm,
          activityPm: this.activityPm,
          assessment: this.assessment,
          support: this.support,
          status: "applied",
          appliedBy: this.appliedBy,
        });
        this.isSaving = false;

        this.$router.push({ name: "ClassDiaries" });
      }
    },
    async createAsDraft() {
      if (this.classId) {
        this.isSaving = true;
        await classDiaryModle.createDiary({
          classId: this.classId,
          date: this.date,
          aim: this.aim,
          activityAm: this.activityAm,
          activityPm: this.activityPm,
          assessment: this.assessment,
          support: this.support,
          status: "draft",
        });
        this.isSaving = false;

        this.$router.push({ name: "ClassDiaries" });
      }
    },
    async checkExistance() {
      if (this.classId && this.date) {
        const find = await ApiClassDiary.getClassDiary({
          classId: this.classId,
          date: this.date,
        });
        if (find) {
          this.existingDiary = find;
        } else {
          this.existingDiary = null;
        }
      }
    },
  },

  data: (): DataType => {
    return {
      classId: null,
      date: dayjs().format("YYYY-MM-DD"),
      aim: "",
      activityAm: "",
      activityPm: "",
      assessment: "",
      support: "",
      isSaving: false,
      appliedBy: null,

      existingError: false,
      existingDiary: null,
    };
  },

  watch: {
    /*
    classId: {
      handler() {
        this.checkExistance();
      },
    },
    */
    date: {
      handler() {
        this.checkExistance();
        nurseryUserModule.listStaffs({ date: this.date });
      },
    },
  },

  mounted() {
    const nurseryId = LocalDataService.getNurseryId();
    if (nurseryId) {
      classModule.listClass({
        nurseryId: nurseryId,
      });
      nurseryUserModule.listStaffs({ date: this.date });
    }

    const classId = this.$route.query.classId;
    if (classId) {
      try {
        const parsed = parseInt(classId as string);
        this.classId = parsed;
      } catch {
        return;
      }
    }
  },
});
</script>
<style scoped>
.existing-error-message {
  font-weight: bold;
  color: #e53935;
}
</style>
